// src/screens/LiveMatchesScreen.js
// TV Guide / ESPN-style Live Matches Browser with Tsunami indicators
// Features:
// - Live streams pulled from active match participants
// - Promo videos for games with no active events
// - Ad placements served from adService.js (Firestore-backed, demo fallback)


import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  Image,
  Pressable,
  ActivityIndicator,
  StyleSheet,
  Dimensions,
  RefreshControl,
  ScrollView,
  SafeAreaView,
  Animated,
  Platform,
  Linking,
} from 'react-native';
import { WebView } from 'react-native-webview';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { db } from '../../firebaseConfig';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { TsunamiIndicator } from '../components/TsunamiMeter';
import StartingSoonRail from '../components/StartingSoonRail';
import { GAME_PROFILES } from '../constants/gameData';
import { useGameAvailability } from '../hooks/useGameAvailability';
import useCritsEnabled from '../hooks/useCritsEnabled';
import { useAds, AdBanner as AdBannerService, AdCardCompact } from '../services/adService';

// ---------- Game Images ----------
// Images stored in Firebase Storage at: gs://critcash-45968.firebasestorage.app/gameImages/{abbr}.webp
// (converted from the original PNGs 2026-07-09 — ~89% smaller; PNGs kept in
// the bucket as fallback/source)

// Your Firebase Storage bucket
const FIREBASE_STORAGE_BUCKET = 'critcash-45968.firebasestorage.app';

// Helper to get game image URL from Firebase Storage
function getGameImageUrl(abbr) {
  // Firebase Storage public URL format
  const encodedPath = encodeURIComponent(`gameImages/${abbr}.webp`);
  return `https://firebasestorage.googleapis.com/v0/b/${FIREBASE_STORAGE_BUCKET}/o/${encodedPath}?alt=media`;
}

// ---------- Game Promo Videos ----------
// Promo videos for each game - stored in Firebase Storage or external YouTube embeds
// These are displayed when no live events exist for a game category
const GAME_PROMO_VIDEOS = {
  sf6: {
    type: 'youtube',
    videoId: '1INU3FOJsTw',
    title: 'Street Fighter 6 - Official Trailer',
    description: 'Master the art of fighting in Street Fighter 6',
    storeUrl: 'https://store.steampowered.com/app/1364780/Street_Fighter_6/',
    storeName: 'Steam',
  },
  tk8: {
    type: 'youtube',
    videoId: '_MM4clV2qjE',
    title: 'Tekken 8 - Official Trailer',
    description: 'The King of Iron Fist Tournament returns',
    storeUrl: 'https://store.steampowered.com/app/1778820/TEKKEN_8/',
    storeName: 'Steam',
  },
  mk1: {
    type: 'youtube',
    videoId: 'lBYvt1_t6Q4',
    title: 'Mortal Kombat 1 - Official Trailer',
    description: 'A new era of Kombat begins',
    storeUrl: 'https://store.steampowered.com/app/1971870/Mortal_Kombat_1/',
    storeName: 'Steam',
  },
  ssb: {
    type: 'youtube',
    videoId: 'EXnbMp1yr1k',
    title: 'Super Smash Bros. Ultimate',
    description: 'Everyone is here!',
    storeUrl: 'https://www.nintendo.com/us/store/products/super-smash-bros-ultimate-switch/',
    storeName: 'Nintendo eShop',
  },
  val: {
    type: 'youtube',
    videoId: 'GAkFklRvhjU',
    title: 'Valorant - Official Trailer',
    description: 'A 5v5 character-based tactical shooter',
    storeUrl: 'https://playvalorant.com/',
    storeName: 'Riot',
  },
  cod: {
    type: 'youtube',
    videoId: 'pGFYtZD53ZA',
    title: 'Call of Duty - Official Trailer',
    description: 'The ultimate first-person shooter experience',
    storeUrl: 'https://store.steampowered.com/app/1938090/Call_of_Duty/',
    storeName: 'Steam',
  },
  cs2: {
    type: 'youtube',
    videoId: 'nSE38xjMLqE',
    title: 'Counter Strike 2 - Official Trailer',
    description: 'The ultimate first-person shooter experience',
    storeUrl: 'https://store.steampowered.com/app/730/CounterStrike_2/',
    storeName: 'Steam',
  },
  rkl: {
    type: 'youtube',
    videoId: 'SgSX3gOrj60',
    title: 'Rocket League - Official Trailer',
    description: 'Soccer meets rocket-powered cars',
    storeUrl: 'https://www.epicgames.com/store/en-US/p/rocket-league',
    storeName: 'Epic',
  },
  lol: {
    type: 'youtube',
    videoId: '-3aes7Vh8cc',
    title: 'League of Legends - Official Trailer',
    description: 'The world\'s most popular MOBA',
    storeUrl: 'https://www.leagueoflegends.com/',
    storeName: 'Riot',
  },
  dt2: {
    type: 'youtube',
    videoId: 'SmnqsdeHFT0',
    title: 'DOTA 2 - Official Trailer',
    description: 'Join the Battle',
    storeUrl: 'https://store.steampowered.com/app/570/Dota_2/',
    storeName: 'Steam',
  },
  mvr: {
    type: 'youtube',
    videoId: '-b0veB7q9P4',
    title: 'Marvel Rivals - Official Trailer',
    description: 'Join the Battle',
    storeUrl: 'https://store.steampowered.com/app/2767030/Marvel_Rivals/',
    storeName: 'Steam',
  },
  ow2: {
    type: 'youtube',
    videoId: 'dZl1yGUetjI',
    title: 'Overwatch 2',
    description: 'Jump into the Battle',
    storeUrl: 'https://store.steampowered.com/app/2357570/Overwatch_2/',
    storeName: 'Steam',
  },
  n2k: {
    type: 'youtube',
    // Mh57YWNaQC4 stopped embedding (YouTube oEmbed 403, 2026-10-14): cover image until a working trailer id is set.
    videoId: null,
    title: 'NBA 2K',
    description: 'Experience the thrill of the NBA',
    storeUrl: 'https://store.steampowered.com/app/2338770/NBA_2K25/',
    storeName: 'Steam',
  },
  mdn: {
    type: 'youtube',
    videoId: 'EZrgXXHSaBQ',
    title: 'Madden NFL - Official Trailer',
    description: 'Own the gridiron',
    storeUrl: 'https://www.ea.com/games/madden-nfl',
    storeName: 'Steam',
  },
  '2xk': {
    type: 'youtube',
    videoId: 'TrUde1RDyM4',
    title: '2XKO - Official Trailer',
    description: 'Tag team fighting from Riot Games',
    storeUrl: 'https://2xko.riotgames.com/',
    storeName: 'Riot',
  },
  mtf: {
    type: 'youtube',
    videoId: '6fbfrV5qqnU',
    title: 'MARVEL Tokon: Fighting Souls',
    description: 'Marvel heroes clash',
    storeUrl: 'https://www.marvel.com/',
    storeName: 'Coming Soon',
  },
  rb6: {
    type: 'youtube',
    videoId: 'BI9fgQY0d5I',
    title: 'Rainbow 6 Siege X',
    description: 'Rienforce, Difuse, Breach',
    storeUrl: 'https://store.steampowered.com/app/359550/Tom_Clancys_Rainbow_Six_Siege_X/',
    storeName: 'Steam',
  },
  apl: {
    type: 'youtube',
    videoId: 'UMJb_mkqynU',
    title: 'Apex Legends',
    description: 'Ready Up. Survive',
    storeUrl: 'https://store.steampowered.com/app/1172470/Apex_Legends/',
    storeName: 'Steam',
  },
  irc: {
    type: 'youtube',
    videoId: 'FA1DHaXPk7c',
    title: 'iRacing',
    description: 'Drive to Win.',
    storeUrl: 'https://store.steampowered.com/app/266410/iRacing/',
    storeName: 'Steam',
  },
  gt7: {
    type: 'youtube',
    videoId: '1tBUsXIkG1A',
    title: 'Gran Turismo 7 - Official Trailer',
    description: 'Battle for Each Overtake',
    storeUrl: 'https://www.playstation.com/en-us/games/gran-turismo-7/',
    storeName: 'Playstation',
  },
};

// Default promo for games without a specific video
const DEFAULT_PROMO = {
  type: 'youtube',
  // No platform promo video yet (founder 2026-10-14): no videoId, so the card
  // shows the game's cover image. Never a placeholder video here.
  videoId: null,
  title: 'CritCash - Go live. The rest is built in.',
  description: 'Stream any game, save every stream as a video, and run your own events.',
  storeUrl: null,
  storeName: null,
};

// ---------- Constants ----------
const { width: SCREEN_WIDTH } = Dimensions.get('window');
const IS_WIDE = SCREEN_WIDTH > 900;

const EVENT_CARD_W = IS_WIDE ? 260 : 180;
const EVENT_CARD_H = Math.round(EVENT_CARD_W * 9 / 16);
const CHANNEL_COL_W = IS_WIDE ? (EVENT_CARD_W + 16) : 120;
const CARD_GAP = 12;
const ROW_GAP = 12;

// Build supported games list from GAME_PROFILES
const SUPPORTED_GAMES = Object.entries(GAME_PROFILES).map(([id, profile]) => ({
  id,
  abbr: profile.abbr,
  name: profile.displayName,
  type: profile.type,
}));

// Filter options
const GENRE_FILTERS = ['All', 'Fighting', 'Shooter', 'Sports', 'Racing', 'MOBA'];
const PRIZE_FILTERS = [
  { label: 'All', min: 0 },
  { label: '1k+ Crits', min: 100000 },
  { label: '5k+ Crits', min: 500000 },
  { label: '25k+ Crits', min: 2500000 },
];





// ---------- Helpers ----------
// Post-pivot: event prizes are Crits (whole units, not cents).
function formatPrize(units) {
  const n = Math.max(0, Math.floor(Number(units || 0)));
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M Crits`;
  if (n >= 1_000) return `${Math.floor(n / 1_000)}K Crits`;
  return `${n} ${n === 1 ? 'Crit' : 'Crits'}`;
}

// Returns the headline prize in WHOLE CRITS (post-pivot). The maps below set
// payout.firstPrizeCents in Crit units.
function getFirstPrizeCents(event) {
  if (event?.payout?.firstPrizeCents) return event.payout.firstPrizeCents;
  if (typeof event?.firstCrit === 'number') return event.firstCrit;
  const pool = event?.critPotCount || 0;
  return pool > 0 ? Math.floor(pool * 0.18) : 0;
}

function getEventThumbUrl(e) {
  if (e?.thumbUrl) return e.thumbUrl;
  // Check for active participant stream thumbnail
  if (e?.activeStream?.thumbUrl) return e.activeStream.thumbUrl;
  
  // YouTube thumbnail
  const ytId = e?.stream?.videoId || e?.activeStream?.videoId;
  if ((e?.stream?.provider === 'youtube' || e?.activeStream?.provider === 'youtube') && ytId) {
    return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
  }
  
  // Twitch thumbnail
  if (e?.activeStream?.provider === 'twitch' && e?.activeStream?.channelName) {
    return `https://static-cdn.jtvnw.net/previews-ttv/live_user_${e.activeStream.channelName.toLowerCase()}-320x180.jpg`;
  }
  
  // CritCash stream - use game image as fallback
  // Snapshot API available at /api/snapshot/{streamKey} when enabled
  if (e?.activeStream?.provider === 'critcash') {
    const gameId = e?.game?.id || e?.gameId;
    if (gameId) {
      return getGameImageUrl(GAME_PROFILES[gameId]?.abbr || gameId);
    }
  }
  
  // Fallback to game image
  const gameId = e?.game?.id || e?.gameId;
  if (gameId) {
    return getGameImageUrl(GAME_PROFILES[gameId]?.abbr || gameId);
  }
  
  return null;
}

function getViewerCount(e) {
  return e?.viewerCount || e?.stream?.viewerCount || e?.activeStream?.viewerCount || 0;
}

function getTimeElapsed(startedAt) {
  if (!startedAt?.toDate) return '';
  const mins = Math.floor((Date.now() - startedAt.toDate().getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  return `${hours}h ${remainingMins}m`;
}

// Get embed URL for a stream
function getStreamEmbedUrl(stream) {
  if (!stream) return null;
  if (stream.provider === 'youtube' && stream.videoId) {
    return `https://www.youtube.com/embed/${stream.videoId}?autoplay=1&mute=1`;
  }
  if (stream.provider === 'twitch' && stream.channelName) {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    return `https://player.twitch.tv/?channel=${stream.channelName}&parent=${hostname}&muted=true`;
  }
  if (stream.provider === 'critcash' && stream.playbackUrl) {
    return stream.playbackUrl;
  }
  return null;
}

// Get YouTube embed URL for promo video
function getPromoEmbedUrl(promo) {
  if (!promo) return null;
  if (promo.type === 'youtube' && promo.videoId) {
    return `https://www.youtube.com/embed/${promo.videoId}?autoplay=1&mute=1&loop=1&playlist=${promo.videoId}`;
  }
  if (promo.type === 'firebase' && promo.path) {
    const encodedPath = encodeURIComponent(promo.path);
    return `https://firebasestorage.googleapis.com/v0/b/${FIREBASE_STORAGE_BUCKET}/o/${encodedPath}?alt=media`;
  }
  return null;
}

// ---------- Components ----------

// Filter Section (Collapsible)
const FilterSection = ({ 
  genre, 
  setGenre, 
  prizeMin, 
  setPrizeMin, 
  showAllGames, 
  setShowAllGames,
  isExpanded,
  onClear,
  availableGenres,
  // Evergreen 2.3 flag, passed from the screen (one listener). Defaults TRUE
  // so an unpassed prop can never hide Crit UI in ON mode.
  critsEnabled = true,
}) => {
  const hasActiveFilters = genre !== 'All' || prizeMin > 0;

  if (!isExpanded) return null;

  const genresToShow = availableGenres || GENRE_FILTERS;

  return (
    <Animated.View style={styles.filterSection}>
      {/* Genre Filters */}
      <View style={styles.filterGroup}>
        <Text style={styles.filterGroupLabel}>GENRE</Text>
        <View style={styles.filterChips}>
          {genresToShow.map(g => (
            <Pressable
              key={g}
              style={[styles.chip, genre === g && styles.chipActive]}
              onPress={() => setGenre(g)}
            >
              <Text style={[styles.chipText, genre === g && styles.chipTextActive]}>{g}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      {/* Prize Filters */}
      {critsEnabled && (
      <View style={styles.filterGroup}>
        <Text style={styles.filterGroupLabel}>MINIMUM PRIZE</Text>
        <View style={styles.filterChips}>
          {PRIZE_FILTERS.map(p => (
            <Pressable
              key={p.label}
              style={[styles.chip, prizeMin === p.min && styles.chipActive]}
              onPress={() => setPrizeMin(p.min)}
            >
              <Text style={[styles.chipText, prizeMin === p.min && styles.chipTextActive]}>
                {p.label}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
      )}

      {/* Show All Games Toggle */}
      <View style={styles.filterToggleRow}>
        <Text style={styles.filterToggleLabel}>Show games with no live events</Text>
        <Pressable
          style={[styles.toggle, showAllGames && styles.toggleActive]}
          onPress={() => setShowAllGames(!showAllGames)}
        >
          <View style={[styles.toggleKnob, showAllGames && styles.toggleKnobActive]} />
        </Pressable>
      </View>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Pressable style={styles.clearFiltersBtn} onPress={onClear}>
          <Text style={styles.clearFiltersText}>Clear Filters</Text>
        </Pressable>
      )}
    </Animated.View>
  );
};



// Create Event CTA Card
const CreateEventCard = ({ game, onPress }) => (
  <Pressable style={styles.createEventCard} onPress={onPress}>
    <View style={styles.createEventIcon}>
      <Text style={styles.createEventIconText}>+</Text>
    </View>
    <Text style={styles.createEventText}>Create an Event</Text>
    <Text style={styles.createEventSubtext}>Grow the {game?.name || 'game'} scene</Text>
  </Pressable>
);

// Promo Video Card - Shows game trailer when no events are live
const PromoVideoCard = ({ game, navigation }) => {
  const promo = GAME_PROMO_VIDEOS[game?.id] || DEFAULT_PROMO;
  const embedUrl = getPromoEmbedUrl(promo);

  const handleBuyGame = () => {
    if (promo.storeUrl) {
      // Open store link
      if (typeof window !== 'undefined') {
        window.open(promo.storeUrl, '_blank');
      }
    }
  };

  const handleCreateEvent = () => {
    navigation.navigate('CreateTournament', { gameId: game.id });
  };

  return (
    <View style={styles.promoCard}>
      {/* Video Thumbnail/Embed */}
      <View style={styles.promoVideoWrap}>
        {embedUrl ? (
          <View style={styles.promoVideoEmbed}>
            <iframe
              src={embedUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </View>
        ) : (
          <Image
            source={{ uri: getGameImageUrl(game.abbr) }}
            style={styles.promoVideoFallback}
            resizeMode="cover"
          />
        )}

        {/* Overlay badge */}
        <View style={styles.promoBadge}>
          <Text style={styles.promoBadgeText}>NO LIVE EVENTS</Text>
        </View>
      </View>

      {/* Promo Info */}
      <View style={styles.promoInfo}>
        <Text style={styles.promoTitle} numberOfLines={1}>
          {promo.title || `${game?.name} Promo`}
        </Text>
        <Text style={styles.promoDescription} numberOfLines={2}>
          {promo.description || 'Check out this game!'}
        </Text>

        {/* CTA Buttons */}
        <View style={styles.promoCTARow}>
          {promo.storeUrl && (
            <Pressable style={styles.promoBuyBtn} onPress={handleBuyGame}>
              <Text style={styles.promoBuyBtnText}>
                {promo.storeName === 'Free to Play' ? 'Play Free' : `Get on ${promo.storeName}`}
              </Text>
            </Pressable>
          )}
          <Pressable style={styles.promoCreateBtn} onPress={handleCreateEvent}>
            <Text style={styles.promoCreateBtnText}>Host Event</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
};

// Event Card with Live Participant Stream - Hover/Press to Play
const EventCard = ({ event, onPress, critsEnabled = true }) => {
  const [isHovering, setIsHovering] = useState(false);
  const thumb = getEventThumbUrl(event);
  const prize = formatPrize(getFirstPrizeCents(event));
  const viewers = getViewerCount(event);
  const elapsed = getTimeElapsed(event.startedAt);
  const floodTotal = event?.critFloodCount || 0; // flood mid-redesign → hidden until Crit-based
  const activeStreamer = event?.activeStream?.displayName;
  
  // Check if this event has a CritCash stream we can play
  const hasCritCashStream = event?.activeStream?.provider === 'critcash' && event?.activeStream?.playbackUrl;
  const streamUrl = event?.activeStream?.playbackUrl;
  
  // Only show streamer name if it's not generic
  const showStreamerName = activeStreamer && 
    activeStreamer !== 'Streamer' && 
    activeStreamer !== 'CritCash' &&
    !hasCritCashStream; // Don't show streamer badge if we're showing CritCash badge

  // HLS player HTML for iframe (web only)
  const hlsPlayerHtml = streamUrl ? `
    <!DOCTYPE html>
    <html>
    <head>
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
      <script src="https://cdn.jsdelivr.net/npm/hls.js@latest"></script>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html, body { width: 100%; height: 100%; overflow: hidden; background: #1a1a1a; }
        video { width: 100%; height: 100%; object-fit: cover; background: #1a1a1a; }
        .loading { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: #666; font-family: sans-serif; font-size: 12px; }
      </style>
    </head>
    <body>
      <div class="loading">Loading...</div>
      <video id="video" autoplay muted playsinline></video>
      <script>
        var video = document.getElementById('video');
        var loading = document.querySelector('.loading');
        var src = '${streamUrl}';
        
        function hideLoading() {
          loading.style.display = 'none';
        }
        
        if (Hls.isSupported()) {
          var hls = new Hls({ 
            enableWorker: true, 
            lowLatencyMode: true,
            maxBufferLength: 10,
            maxMaxBufferLength: 20
          });
          hls.loadSource(src);
          hls.attachMedia(video);
          hls.on(Hls.Events.MANIFEST_PARSED, function() { 
            hideLoading();
            video.play(); 
          });
          hls.on(Hls.Events.ERROR, function() {
            loading.textContent = 'Stream loading...';
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = src;
          video.addEventListener('loadedmetadata', function() {
            hideLoading();
            video.play();
          });
        }
      </script>
    </body>
    </html>
  ` : '';

  return (
    <Pressable
      style={styles.eventCard}
      onPress={onPress}
      onHoverIn={() => setIsHovering(true)}
      onHoverOut={() => setIsHovering(false)}
    >
      {/* Thumbnail / Live Stream */}
      <View style={styles.eventThumb}>
        {/* Show live stream when hovering on web, if CritCash stream available */}
        {isHovering && hasCritCashStream && Platform.OS === 'web' ? (
          <iframe
            srcDoc={hlsPlayerHtml}
            style={{ 
              width: '100%', 
              height: '100%', 
              border: 'none',
              backgroundColor: '#1a1a1a',
            }}
            allow="autoplay"
          />
        ) : thumb ? (
          <Image
            source={{ uri: thumb }}
            style={styles.eventThumbImg}
            resizeMode="cover"
          />
        ) : (
          <View style={styles.eventThumbPlaceholder}>
            <Ionicons name="videocam-outline" size={32} color="#333" />
          </View>
        )}

        {/* LIVE Badge */}
        <View style={styles.liveBadge}>
          <View style={styles.liveDot} />
          <Text style={styles.liveText}>LIVE</Text>
        </View>

        {/* CritCash Stream Badge */}
        {hasCritCashStream && (
          <View style={styles.critcashBadge}>
            <Text style={styles.critcashBadgeText}>CRITCASH</Text>
          </View>
        )}

        {/* Streamer Name Badge */}
        {showStreamerName && (
          <View style={styles.streamerBadge}>
            <Text style={styles.streamerText} numberOfLines={1}>{activeStreamer}</Text>
          </View>
        )}
        
        {/* Viewer count */}
        {viewers > 0 && (
          <View style={styles.viewerBadge}>
            <Text style={styles.viewerText}>{viewers.toLocaleString()}</Text>
          </View>
        )}
        
        {/* Time elapsed */}
        {elapsed && (
          <View style={styles.durationBadge}>
            <Text style={styles.durationText}>{elapsed}</Text>
          </View>
        )}

        {/* Hover hint */}
        {!isHovering && hasCritCashStream && (
          <View style={styles.hoverHint}>
            <Text style={styles.hoverHintText}>Hover to preview</Text>
          </View>
        )}
      </View>

      {/* Event Info */}
      <View style={styles.eventInfo}>
        <Text style={styles.eventTitle} numberOfLines={1}>
          {event.title || event.name || 'Live Event'}
        </Text>
        {critsEnabled && (
        <Text style={styles.eventPrize}>Playing for {prize}</Text>
        )}
        {event.entrantsCount && (
          <Text style={styles.eventMeta}>{event.entrantsCount} entrants</Text>
        )}
        
        {/* Tsunami indicator */}
        {critsEnabled && floodTotal > 0 && (
          <View style={styles.tsunamiRow}>
            <TsunamiIndicator 
              currentCents={floodTotal} 
              targetCents={floodTotal * 2} 
              size="small"
            />
            <Text style={styles.tsunamiText}>+{formatPrize(floodTotal)} from viewers</Text>
          </View>
        )}
      </View>
    </Pressable>
  );
};

// Navigation Arrow Component
const NavArrow = ({ direction, visible, onPress }) => {
  if (!visible) return null;
  
  return (
    <Pressable
      style={[
        styles.navArrow,
        direction === 'left' ? styles.navArrowLeft : styles.navArrowRight,
      ]}
      onPress={onPress}
    >
      <Ionicons
        name={direction === 'left' ? 'chevron-back' : 'chevron-forward'}
        size={24}
        color="#fff"
      />
    </Pressable>
  );
};

// Game Channel Row
const GameChannelRow = ({ game, events, navigation, showAdBannerAfter, isGameEnabled, liveParticipantStreams, getNextCardAd, getNextBannerAd, critsEnabled = true }) => {
  const scrollRef = useRef(null);
  const [scrollX, setScrollX] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  
  // Check if this game is enabled
  const gameEnabled = isGameEnabled(game.id);

  // Filter events for this game and attach active streams
  const gameEvents = useMemo(() => {
    return events
      .filter((e) => {
        const eventGameId = (e.game?.id || e.gameId || '').toLowerCase();
        return eventGameId === game.id.toLowerCase();
      })
      .map(event => {
        // Attach active participant stream if available
        const eventStreams = liveParticipantStreams[event.id] || [];
        if (eventStreams.length > 0) {
          return { ...event, activeStream: eventStreams[0] };
        }
        return event;
      });
  }, [events, game.id, liveParticipantStreams]);

  // Build items list (events + ads + create card)
  const items = useMemo(() => {
    const result = [];
    gameEvents.forEach((event, index) => {
      result.push({ type: 'event', key: event.id, data: event });
      
      // Insert ad after every 4 events
      if ((index + 1) % 4 === 0 && index < gameEvents.length - 1) {
        result.push({ type: 'ad', key: `ad-${index}` });
      }
    });
    
    // Add create event card at the end
    result.push({ type: 'create', key: 'create' });
    
    return result;
  }, [gameEvents]);

  // Navigation state
  const showLeftArrow = scrollX > 10;
  const showRightArrow = contentWidth - scrollX - containerWidth > 10;

  const handleScroll = (e) => {
    setScrollX(e.nativeEvent.contentOffset.x);
    setContentWidth(e.nativeEvent.contentSize.width);
    setContainerWidth(e.nativeEvent.layoutMeasurement.width);
  };

  const scrollBy = (amount) => {
    scrollRef.current?.scrollTo({
      x: amount,
      animated: true,
    });
  };

  // Empty state with Promo Video
  if (gameEvents.length === 0) {
    if (!gameEnabled) {
      return null;
    }
    
    return (
      <>
        <View style={styles.channelRow}>
          {/* Row-level paint smoke effect */}
          <View style={styles.rowSmokeContainer}>
            <Image
              source={{ uri: getGameImageUrl(game.abbr) }}
              style={styles.rowSmokeImage}
              resizeMode="cover"
              blurRadius={20}
            />
            <View style={styles.rowSmokeFade} />
            <View style={styles.rowSmokeFadeVertical} />
          </View>
          
          {/* Game Logo Column */}
          <View style={styles.channelCol}>
            <Image
              source={{ uri: getGameImageUrl(game.abbr) }}
              style={styles.channelLogo}
              resizeMode="contain"
            />
          </View>
          <View style={styles.eventsContainer}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.eventsScroll}
            >
              {/* Promo Video Card */}
              <PromoVideoCard game={game} navigation={navigation} />
              {/* Create Event Card */}
              <CreateEventCard
                game={game}
                onPress={() => navigation.navigate('CreateTournament', { gameId: game.id })}
              />
            </ScrollView>
          </View>
        </View>
        {showAdBannerAfter && (() => {
          const ad = getNextBannerAd ? getNextBannerAd() : null;
          return ad ? <AdBannerService ad={ad} placement="live_banner" screen="live_matches" /> : null;
        })()}
      </>
    );
  }

  return (
    <>
      <View style={styles.channelRow}>
        {/* Row-level paint smoke effect */}
        <View style={styles.rowSmokeContainer}>
          <Image
            source={{ uri: getGameImageUrl(game.abbr) }}
            style={styles.rowSmokeImage}
            resizeMode="cover"
            blurRadius={20}
          />
          <View style={styles.rowSmokeFade} />
          <View style={styles.rowSmokeFadeVertical} />
        </View>
        
        {/* Game Logo Column */}
        <View style={styles.channelCol}>
          <Image
            source={{ uri: getGameImageUrl(game.abbr) }}
            style={styles.channelLogo}
            resizeMode="contain"
          />
        </View>

        {/* Events Container */}
        <View style={styles.eventsContainer}>
          <NavArrow
            direction="left"
            visible={showLeftArrow}
            onPress={() => scrollBy(-EVENT_CARD_W * 2)}
          />

          <ScrollView
            ref={scrollRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            contentContainerStyle={styles.eventsScroll}
          >
            {items.map((item) => {
              if (item.type === 'ad') {
                const ad = getNextCardAd ? getNextCardAd() : null;
                if (!ad) return null;
                return <AdCardCompact key={item.key} ad={ad} placement="live_inline" screen="live_matches" />;
              }
              if (item.type === 'create') {
                return (
                  <CreateEventCard
                    key={item.key}
                    game={game}
                    onPress={() => navigation.navigate('CreateTournament', { gameId: game.id })}
                  />
                );
              }
              return (
                <EventCard
                  key={item.key}
                  event={item.data}
                  critsEnabled={critsEnabled}
                  onPress={() => navigation.navigate('EventWatch', { eventId: item.data.id })}
                />
              );
            })}
          </ScrollView>

          <NavArrow
            direction="right"
            visible={showRightArrow}
            onPress={() => scrollBy(EVENT_CARD_W * 2)}
          />
          
          {/* Mobile scroll hint */}
          {showRightArrow && !IS_WIDE && (
            <View style={styles.scrollHintRight}>
              <Text style={styles.scrollHintArrow}>{'>'}</Text>
            </View>
          )}
        </View>
      </View>
      {showAdBannerAfter && (() => {
        const ad = getNextBannerAd ? getNextBannerAd() : null;
        return ad ? <AdBannerService ad={ad} placement="live_banner" screen="live_matches" /> : null;
      })()}
    </>
  );
};

// Featured Event Hero
const FeaturedEvent = ({ event, navigation, critsEnabled = true }) => {
  if (!event) return null;

  const thumb = getEventThumbUrl(event);
  const prize = formatPrize(getFirstPrizeCents(event));
  const viewers = getViewerCount(event);
  const gameName = event.game?.displayName || event.game?.name || event.category || 'Game';
  const floodTotal = event?.critFloodCount || 0; // flood mid-redesign → hidden until Crit-based
  const activeStreamer = event?.activeStream?.displayName;

  return (
    <Pressable
      style={styles.featured}
      onPress={() => navigation.navigate('EventWatch', { eventId: event.id })}
    >
      {thumb && (
        <Image source={{ uri: thumb }} style={styles.featuredThumb} resizeMode="cover" />
      )}
      <View style={styles.featuredOverlay} />

      <View style={styles.featuredContent}>
        <View style={styles.featuredBadges}>
          <View style={styles.featuredLiveBadge}>
            <View style={styles.liveDot} />
            <Text style={styles.featuredLiveText}>LIVE NOW</Text>
          </View>
          {viewers > 0 && (
            <View style={styles.featuredViewerBadge}>
              <Text style={styles.featuredViewerText}> {viewers.toLocaleString()} watching</Text>
            </View>
          )}
          {activeStreamer && (
            <View style={styles.featuredStreamerBadge}>
              <Text style={styles.featuredStreamerText}>Streaming: {activeStreamer}</Text>
            </View>
          )}
        </View>

        <Text style={styles.featuredGame}>{gameName.toUpperCase()}</Text>
        <Text style={styles.featuredTitle} numberOfLines={2}>
          {event.title || event.name || 'Featured Event'}
        </Text>
        {critsEnabled && (
        <Text style={styles.featuredPrize}>{prize} Crit Pool</Text>
        )}

        {critsEnabled && floodTotal > 0 && (
          <View style={styles.featuredFlood}>
            <Text style={styles.featuredFloodText}>
              +{formatPrize(floodTotal)} from viewers
            </Text>
          </View>
        )}
      </View>
    </Pressable>
  );
};

// ---------- Main Component ----------
export default function LiveMatchesScreen({ navigation }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [genre, setGenre] = useState('All');
  const [prizeMin, setPrizeMin] = useState(0);
  const [showAllGames, setShowAllGames] = useState(true);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [liveParticipantStreams, setLiveParticipantStreams] = useState({});
  // Evergreen step 2.3: one settings/economy listener for the whole screen;
  // passed down as a prop so cards never open their own.
  const critsEnabled = useCritsEnabled();
  
  // Game availability - filter out disabled games
  const { isGameEnabled, isGameVisible, isEventVisible, loading: availabilityLoading } = useGameAvailability();
  
  // Ad service hooks
  const { getNextAd: getNextBannerAd } = useAds('live_banner');
  const { getNextAd: getNextCardAd } = useAds('live_inline');

  // Test mode flag
  const TEST_MODE = false;
  
  const MOCK_EVENTS = TEST_MODE ? [
    {
      id: 'test-1',
      title: 'Weekly Championship Series',
      status: 'live',
      game: { id: 'val', abbr: 'VAL', displayName: 'Valorant', type: 'shooter' },
      gameId: 'val',
      category: 'shooter',
      entrantsCount: 128,
      payout: { firstPrizeCents: 2500000 },
      floodTotalCents: 84700,
      viewerCount: 2847,
      startedAt: { toDate: () => new Date(Date.now() - 83 * 60000) },
    },
  ] : [];

  // Check if any filters are active
  const hasActiveFilters = genre !== 'All' || prizeMin > 0;

  // Clear all filters
  const clearFilters = useCallback(() => {
    setGenre('All');
    setPrizeMin(0);
  }, []);

  // Fetch active participant streams for live events
  // Strategy: Get all active CritCash streams from streamKeys, then match them to event entrants
  // Enhancement: Try to use Mux CDN URLs when available for better scalability
  const fetchLiveParticipantStreams = useCallback(async (eventIds) => {
    if (eventIds.length === 0) return;
    
    const streamsMap = {};
    // const HLS_SERVER = 'https://play.critcash.com'; // REMOVED - Mux CDN is the sole playback path
    const functions = getFunctions();
    const getStreamPlaybackUrl = httpsCallable(functions, 'getStreamPlaybackUrl');
    
    try {
      // Step 1: Get ALL active CritCash streams from streamKeys collection
      const streamKeysSnap = await getDocs(
        query(
          collection(db, 'streamKeys'),
          where('status', '==', 'active'),
          limit(50)
        )
      );
      
      // Build a map of userId -> stream data
      const activeStreamsByUser = {};
      for (const streamDoc of streamKeysSnap.docs) {
        const streamData = streamDoc.data();
        const streamKey = streamDoc.id; // Document ID IS the stream key
        const userId = streamData.userId;
        const displayName = streamData.displayName || streamData.ign || streamData.odisplayName;
        
        if (userId) {
          // Mux CDN will provide the playback URL
          let playbackUrl = null;
          let source = 'mux';
          
          // Get Mux CDN playback URL
          try {
            const result = await getStreamPlaybackUrl({
              odisplayName: displayName || userId,
            });

            if (result.data?.available && result.data?.playbackUrl) {
              playbackUrl = result.data.playbackUrl;
              source = result.data.source || 'mux';
              console.log(`[LiveMatches] Using Mux CDN for ${displayName}`);
            }
          } catch (muxErr) {
            // Mux not available — no playback URL for this stream
            console.log(`[LiveMatches] Could not get Mux playback URL for ${displayName}:`, muxErr.message);
          }
          
          activeStreamsByUser[userId] = {
            odUserId: userId,
            displayName: displayName || 'Streamer',
            provider: 'critcash',
            streamKey: streamKey,
            playbackUrl: playbackUrl,
            source: source,
            thumbUrl: streamData.thumbUrl || null,
            viewerCount: streamData.viewerCount || 0,
            startedAt: streamData.startedAt || streamData.lastUsedAt,
          };
        }
      }
      
      // Step 2: For each live event, find entries and match to active streams
      for (const eventId of eventIds) {
        const eventStreams = [];
        
        // Check tournament entries
        try {
          const tournamentEntriesSnap = await getDocs(
            query(
              collection(db, 'tournaments', eventId, 'entries'),
              limit(32)
            )
          );
          
          for (const entryDoc of tournamentEntriesSnap.docs) {
            const entry = entryDoc.data();
            const userId = entry.userId || entryDoc.id;
            
            if (activeStreamsByUser[userId]) {
              eventStreams.push({
                ...activeStreamsByUser[userId],
                entryId: entryDoc.id,
              });
            }
          }
        } catch (e) {
          // Not a tournament, try matches
        }
        
        // Check match entries
        try {
          const matchEntriesSnap = await getDocs(
            query(
              collection(db, 'matches', eventId, 'entries'),
              limit(8)
            )
          );
          
          for (const entryDoc of matchEntriesSnap.docs) {
            const entry = entryDoc.data();
            const userId = entry.userId || entryDoc.id;
            
            if (activeStreamsByUser[userId]) {
              eventStreams.push({
                ...activeStreamsByUser[userId],
                entryId: entryDoc.id,
              });
            }
          }
        } catch (e) {
          // Not a match either
        }
        
        if (eventStreams.length > 0) {
          streamsMap[eventId] = eventStreams;
        }
      }
      
      setLiveParticipantStreams(streamsMap);
    } catch (error) {
      console.error('[LiveMatches] Error fetching participant streams:', error);
    }
  }, []);

  // Subscribe to live events
  useEffect(() => {
    const gameIdToInfo = (gameId) => {
      const profile = GAME_PROFILES[gameId];
      return profile ? {
        id: gameId,
        abbr: profile.abbr,
        displayName: profile.displayName,
        name: profile.displayName,
        type: profile.type,
      } : {
        id: gameId,
        abbr: gameId.toUpperCase(),
        displayName: gameId,
        name: gameId,
        type: 'other',
      };
    };
    
    // Map Firestore doc to normalized event shape
    const mapMatchToEvent = (docSnap) => {
      const data = docSnap.data();
      const gameId = data.game || data.gameId || 'unknown';
      const gameInfo = gameIdToInfo(gameId);
      
      // Calculate Crit prize pool (post-pivot)
      const critEntryCost = Math.max(0, Math.floor(Number(data.critEntryCost || 0)));
      const entriesExpected = data.entriesExpected || 2;
      const entrantsCount = data.entrantsCount || entriesExpected;
      const burnPercent = data.rakePercent || 10;
      const critGross = (Number.isFinite(data.critPotCount) && data.critPotCount > 0)
        ? data.critPotCount
        : critEntryCost * entrantsCount;
      const netPrizeCents = Math.ceil(critGross * (1 - burnPercent / 100));
      const totalPot = netPrizeCents;

      return {
        id: docSnap.id,
        type: 'match',
        title: data.name || data.title || 'H2H Match',
        status: data.status,
        game: gameInfo,
        gameId: gameId,
        category: gameInfo.type,
        entrantsCount: entrantsCount,
        payout: { firstPrizeCents: netPrizeCents },
        potCents: totalPot,
        critPotCount: totalPot,
        floodTotalCents: data.floodTotalCents || 0,
        viewerCount: data.viewerCount || 0,
        startedAt: data.startedAt,
        startAt: data.startAt,
        formatName: data.formatName,
        bracketSize: data.bracketSize,
      };
    };
    
    const mapTournamentToEvent = (docSnap) => {
      const data = docSnap.data();
      const gameId = data.game || data.gameId || 'unknown';
      const gameInfo = gameIdToInfo(gameId);
      
      // Calculate Crit prize pool (post-pivot)
      const critEntryCost = Math.max(0, Math.floor(Number(data.critEntryCost || 0)));
      const entriesExpected = data.bracketSize || data.entriesExpected || 8;
      const entrantsCount = data.entrantsCount || entriesExpected;
      const burnPercent = data.rakePercent || 10;
      const critGross = (Number.isFinite(data.critPotCount) && data.critPotCount > 0)
        ? data.critPotCount
        : critEntryCost * entrantsCount;
      const netPrizeCents = Math.ceil(critGross * (1 - burnPercent / 100));
      const totalPot = netPrizeCents;

      return {
        id: docSnap.id,
        type: 'tournament',
        title: data.name || data.title || 'Tournament',
        status: data.status,
        game: gameInfo,
        gameId: gameId,
        category: gameInfo.type,
        entrantsCount: entrantsCount,
        payout: { firstPrizeCents: netPrizeCents },
        potCents: totalPot,
        critPotCount: totalPot,
        floodTotalCents: data.floodTotalCents || 0,
        viewerCount: data.viewerCount || 0,
        startedAt: data.startedAt,
        startAt: data.startAt,
        formatName: data.formatName,
        bracketSize: data.bracketSize,
      };
    };
    
    let matchEvents = [];
    let tournamentEvents = [];
    let matchesLoaded = false;
    let tournamentsLoaded = false;
    
    const updateEvents = () => {
      const allEvents = [...matchEvents, ...tournamentEvents];
      allEvents.sort((a, b) => {
        const aTime = a.startedAt?.toDate?.() || a.startedAt?.seconds * 1000 || 0;
        const bTime = b.startedAt?.toDate?.() || b.startedAt?.seconds * 1000 || 0;
        return bTime - aTime;
      });
      
      if (TEST_MODE) {
        setEvents([...allEvents, ...MOCK_EVENTS]);
      } else {
        setEvents(allEvents);
      }
      
      // Fetch live participant streams for all events
      const eventIds = allEvents.map(e => e.id);
      fetchLiveParticipantStreams(eventIds);
      
      if (matchesLoaded && tournamentsLoaded) {
        setLoading(false);
      }
    };
    
    const matchesQuery = query(
      collection(db, 'matches'),
      where('status', '==', 'live'),
      limit(100)
    );
    
    const matchesUnsub = onSnapshot(
      matchesQuery,
      (snap) => {
        matchEvents = snap.docs.map(mapMatchToEvent);
        matchesLoaded = true;
        updateEvents();
      },
      (error) => {
        console.error('Error fetching matches:', error);
        matchesLoaded = true;
        updateEvents();
      }
    );
    
    const tournamentsQuery = query(
      collection(db, 'tournaments'),
      where('status', '==', 'live'),
      limit(100)
    );
    
    const tournamentsUnsub = onSnapshot(
      tournamentsQuery,
      (snap) => {
        tournamentEvents = snap.docs.map(mapTournamentToEvent);
        tournamentsLoaded = true;
        updateEvents();
      },
      (error) => {
        console.error('Error fetching tournaments:', error);
        tournamentsLoaded = true;
        updateEvents();
      }
    );

    return () => {
      matchesUnsub();
      tournamentsUnsub();
    };
  }, [fetchLiveParticipantStreams]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    // Re-fetch streams
    const eventIds = events.map(e => e.id);
    fetchLiveParticipantStreams(eventIds).then(() => {
      setRefreshing(false);
    });
  }, [events, fetchLiveParticipantStreams]);

  // Filter events (testing games = admins only, hidden games = nobody)
  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (!isEventVisible(e)) return false;
      const g = (e?.category || e?.genre || e?.game?.type || '').toLowerCase();
      const okGenre = genre === 'All' || g === genre.toLowerCase();
      const first = getFirstPrizeCents(e);
      const okPrize = first >= prizeMin;
      return okGenre && okPrize;
    });
  }, [events, genre, prizeMin, isEventVisible]);

  // Featured event (highest prize pool) with active stream attached
  const featuredEvent = useMemo(() => {
    if (filtered.length === 0) return null;
    const sorted = [...filtered].sort((a, b) => getFirstPrizeCents(b) - getFirstPrizeCents(a));
    const featured = sorted[0];
    // Attach active stream if available
    const eventStreams = liveParticipantStreams[featured.id] || [];
    if (eventStreams.length > 0) {
      return { ...featured, activeStream: eventStreams[0] };
    }
    return featured;
  }, [filtered, liveParticipantStreams]);

  // Available genres
  const availableGenres = useMemo(() => {
    const genres = ['All'];
    
    GENRE_FILTERS.slice(1).forEach(genreFilter => {
      const hasEnabledGame = SUPPORTED_GAMES.some(game => {
        const matchesGenre = game.type.toLowerCase() === genreFilter.toLowerCase();
        const gameEnabled = isGameEnabled(game.id);
        return matchesGenre && gameEnabled;
      });
      
      if (hasEnabledGame) {
        genres.push(genreFilter);
      }
    });
    
    return genres;
  }, [isGameEnabled]);

  // Reset genre to 'All' if current genre becomes unavailable
  useEffect(() => {
    if (genre !== 'All' && !availableGenres.includes(genre)) {
      setGenre('All');
    }
  }, [availableGenres, genre]);

  // Games to show
  const visibleGames = useMemo(() => {
    let games = SUPPORTED_GAMES;
    
    if (genre !== 'All') {
      games = games.filter(g => g.type.toLowerCase() === genre.toLowerCase());
    }
    
    const gamesWithEvents = new Set();
    filtered.forEach(e => {
      const eventGameId = (e.game?.id || e.gameId || '').toLowerCase();
      gamesWithEvents.add(eventGameId);
    });
    
    games = games.filter(g => {
      // Visibility gate first — a testing/hidden game's row never renders for
      // a viewer who can't see it, even if events slipped through upstream.
      if (!isGameVisible(g.id)) return false;

      const hasEvents = gamesWithEvents.has(g.id.toLowerCase());
      const gameEnabled = isGameEnabled(g.id);

      if (hasEvents) return true;

      return gameEnabled && showAllGames;
    });

    return games;
  }, [genre, filtered, showAllGames, isGameEnabled, isGameVisible]);

  const liveCount = filtered.length;

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />

      {/* Top Bar with Filter Toggle */}
      <View style={styles.topBar}>
        <View style={styles.liveCounter}>
          <View style={styles.liveCounterDot} />
          <Text style={styles.liveCounterText}>{liveCount} Live Now</Text>
        </View>

        <Pressable
          style={[styles.filterBtn, (hasActiveFilters || filtersExpanded) && styles.filterBtnActive]}
          onPress={() => setFiltersExpanded(!filtersExpanded)}
        >
          <Ionicons 
            name="options-outline" 
            size={16} 
            color={(hasActiveFilters || filtersExpanded) ? '#000' : '#fff'} 
          />
          <Text style={[styles.filterBtnText, (hasActiveFilters || filtersExpanded) && styles.filterBtnTextActive]}>
            Filters
          </Text>
          {hasActiveFilters && (
            <View style={styles.filterBadge}>
              <Text style={styles.filterBadgeText}>
                {(genre !== 'All' ? 1 : 0) + (prizeMin > 0 ? 1 : 0)}
              </Text>
            </View>
          )}
          <Ionicons 
            name={filtersExpanded ? 'chevron-up' : 'chevron-down'} 
            size={14} 
            color={(hasActiveFilters || filtersExpanded) ? '#000' : '#888'} 
          />
        </Pressable>
      </View>

      {/* Collapsible Filters */}
      <FilterSection
        genre={genre}
        setGenre={setGenre}
        prizeMin={prizeMin}
        setPrizeMin={setPrizeMin}
        showAllGames={showAllGames}
        setShowAllGames={setShowAllGames}
        isExpanded={filtersExpanded}
        onClear={clearFilters}
        availableGenres={availableGenres}
        critsEnabled={critsEnabled}
      />

      {/* Main Content */}
      <ScrollView
        style={styles.body}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#FCD000" />
        }
      >
        {(loading || availabilityLoading) ? (
          <View style={styles.center}>
            <ActivityIndicator size="large" color="#FCD000" />
          </View>
        ) : (
          <>
            {/* Featured Event - only show if we have live events */}
            {featuredEvent && (
              <FeaturedEvent event={featuredEvent} navigation={navigation} critsEnabled={critsEnabled} />
            )}

            {/* Events in their pre-race ceremony window. This screen only ever
                queried status == 'live', so a staged event was invisible here —
                which made the whole pre-event sequence undiscoverable. Renders
                nothing when there's nothing staged. */}
            <StartingSoonRail navigation={navigation} />

            {/* Section Header */}
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>
                {filtered.length > 0 ? 'Live Channels' : 'Browse Games'}
              </Text>
              <Text style={styles.sectionSubtitle}>
                {filtered.length > 0 
                  ? 'Scroll to browse events by game' 
                  : 'No live events right now - check out these games or host your own event'}
              </Text>
            </View>

            {/* Game Channel Rows - always show when games are visible */}
            {visibleGames.length > 0 ? (
              <View style={styles.channelGrid}>
                {visibleGames.map((game, index) => (
                  <GameChannelRow
                    key={game.id}
                    game={game}
                    events={filtered}
                    navigation={navigation}
                    showAdBannerAfter={(index + 1) % 3 === 0 && index < visibleGames.length - 1}
                    isGameEnabled={isGameEnabled}
                    liveParticipantStreams={liveParticipantStreams}
                    getNextCardAd={getNextCardAd}
                    getNextBannerAd={getNextBannerAd}
                    critsEnabled={critsEnabled}
                  />
                ))}
              </View>
            ) : (
              <View style={styles.center}>
                <Text style={styles.emptyText}>No games available with current filters.</Text>
                <Pressable style={styles.clearFiltersInlineBtn} onPress={clearFilters}>
                  <Text style={styles.clearFiltersInlineText}>Clear Filters</Text>
                </Pressable>
              </View>
            )}
          </>
        )}

        <View style={{ height: 100 }} />
      </ScrollView>

      <Footer />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ---------- Styles ----------
const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0a0a0a',
  },

  // Top Bar
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#1a1a1a',
  },
  liveCounter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  liveCounterDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#e11900',
  },
  liveCounterText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  filterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#1a1a1a',
    borderWidth: 1,
    borderColor: '#333',
  },
  filterBtnActive: {
    backgroundColor: '#FCD000',
    borderColor: '#FCD000',
  },
  filterBtnText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
  },
  filterBtnTextActive: {
    color: '#000',
  },
  filterBadge: {
    backgroundColor: '#e11900',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 4,
  },
  filterBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '800',
  },

  // Filter Section
  filterSection: {
    padding: 16,
    backgroundColor: '#111',
    borderBottomWidth: 1,
    borderBottomColor: '#1a1a1a',
  },
  filterGroup: {
    marginBottom: 16,
  },
  filterGroupLabel: {
    color: '#888',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 8,
  },
  filterChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#1a1a1a',
    borderWidth: 1,
    borderColor: '#333',
  },
  chipActive: {
    backgroundColor: '#FCD000',
    borderColor: '#FCD000',
  },
  chipText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  chipTextActive: {
    color: '#000',
  },
  filterToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  filterToggleLabel: {
    color: '#aaa',
    fontSize: 13,
  },
  toggle: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#333',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  toggleActive: {
    backgroundColor: '#FCD000',
  },
  toggleKnob: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#666',
  },
  toggleKnobActive: {
    backgroundColor: '#000',
    alignSelf: 'flex-end',
  },
  clearFiltersBtn: {
    marginTop: 16,
    alignSelf: 'flex-start',
  },
  clearFiltersText: {
    color: '#FCD000',
    fontSize: 13,
    fontWeight: '700',
  },

  // Body
  body: {
    flex: 1,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    color: '#666',
    fontSize: 14,
  },
  clearFiltersInlineBtn: {
    marginTop: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#1a1a1a',
    borderRadius: 6,
  },
  clearFiltersInlineText: {
    color: '#FCD000',
    fontSize: 13,
    fontWeight: '700',
  },

  // Section Header
  sectionHeader: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '900',
    marginBottom: 4,
  },
  sectionSubtitle: {
    color: '#666',
    fontSize: 13,
  },

  // Channel Grid
  channelGrid: {
    gap: ROW_GAP,
  },

  // Channel Row
  channelRow: {
    flexDirection: 'row',
    minHeight: EVENT_CARD_H + 80,
    position: 'relative',
    overflow: 'hidden',
  },
  rowSmokeContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
  },
  rowSmokeImage: {
    position: 'absolute',
    top: -20,
    left: -20,
    width: CHANNEL_COL_W + 40,
    height: EVENT_CARD_H + 120,
    opacity: 0.3,
  },
  rowSmokeFade: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    // Horizontal gradient from left
    backgroundImage: 'linear-gradient(to right, transparent 0%, #0a0a0a 60%)',
  },
  rowSmokeFadeVertical: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    // Vertical gradient
    backgroundImage: 'linear-gradient(to bottom, rgba(10,10,10,0.3) 0%, rgba(10,10,10,0.8) 100%)',
  },
  channelCol: {
    width: CHANNEL_COL_W,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    zIndex: 1,
  },
  channelLogo: {
    width: IS_WIDE ? 140 : 100,
    height: IS_WIDE ? 180 : 140,
    borderRadius: 8,
  },

  // Events Container
  eventsContainer: {
    flex: 1,
    position: 'relative',
  },
  eventsScroll: {
    paddingVertical: 16,
    paddingHorizontal: 8,
    gap: CARD_GAP,
  },
  navArrow: {
    position: 'absolute',
    top: '50%',
    marginTop: -20,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.8)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  navArrowLeft: {
    left: 8,
  },
  navArrowRight: {
    right: 8,
  },
  scrollHintRight: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 40,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(10,10,10,0.8)',
  },
  scrollHintArrow: {
    color: '#666',
    fontSize: 20,
    fontWeight: '300',
  },

  // Event Card
  eventCard: {
    width: EVENT_CARD_W,
    backgroundColor: '#111',
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#2a2a2a',
  },
  eventThumb: {
    width: '100%',
    height: EVENT_CARD_H,
    backgroundColor: '#1a1a1a',
    position: 'relative',
  },
  eventThumbImg: {
    width: '100%',
    height: '100%',
  },
  eventThumbPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Badges
  liveBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e11900',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    gap: 4,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#fff',
  },
  liveText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '900',
  },
  streamerBadge: {
    position: 'absolute',
    top: 36,
    right: 8,
    backgroundColor: 'rgba(252, 208, 0, 0.9)',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
    maxWidth: 100,
  },
  streamerText: {
    color: '#000',
    fontSize: 10,
    fontWeight: '700',
  },
  critcashBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#FCD000',
  },
  critcashBadgeText: {
    color: '#FCD000',
    fontSize: 10,
    fontWeight: '800',
  },
  hoverHint: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingVertical: 6,
    alignItems: 'center',
  },
  hoverHintText: {
    color: '#aaa',
    fontSize: 10,
    fontWeight: '600',
  },
  viewerBadge: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    backgroundColor: 'rgba(0,0,0,0.75)',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
  },
  viewerText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
  },
  durationBadge: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0,0,0,0.75)',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
  },
  durationText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
  },

  // Event Info
  eventInfo: {
    padding: 10,
  },
  eventTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '800',
  },
  eventPrize: {
    color: '#FCD000',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 2,
  },
  eventMeta: {
    color: '#666',
    fontSize: 11,
    marginTop: 4,
  },
  tsunamiRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
    gap: 6,
  },
  tsunamiText: {
    color: '#00CED1',
    fontSize: 10,
    fontWeight: '600',
  },


  // Create Event CTA
  createEventCard: {
    width: 180,
    height: EVENT_CARD_H + 60,
    backgroundColor: '#111',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#333',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  createEventIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#222',
    borderWidth: 2,
    borderColor: '#444',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  createEventIconText: {
    color: '#666',
    fontSize: 24,
    fontWeight: '300',
  },
  createEventText: {
    color: '#666',
    fontSize: 13,
    fontWeight: '700',
  },
  createEventSubtext: {
    color: '#444',
    fontSize: 11,
  },

  // Promo Video Card
  promoCard: {
    width: EVENT_CARD_W + 60,
    backgroundColor: '#111',
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#2a2a2a',
  },
  promoVideoWrap: {
    width: '100%',
    height: EVENT_CARD_H + 20,
    backgroundColor: '#0a0a0a',
    position: 'relative',
  },
  promoVideoEmbed: {
    width: '100%',
    height: '100%',
  },
  promoVideoFallback: {
    width: '100%',
    height: '100%',
  },
  promoBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    backgroundColor: 'rgba(0,0,0,0.8)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  promoBadgeText: {
    color: '#888',
    fontSize: 9,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  promoInfo: {
    padding: 12,
  },
  promoTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '800',
    marginBottom: 4,
  },
  promoDescription: {
    color: '#888',
    fontSize: 12,
    marginBottom: 12,
    lineHeight: 16,
  },
  promoCTARow: {
    flexDirection: 'row',
    gap: 8,
  },
  promoBuyBtn: {
    flex: 1,
    backgroundColor: '#FCD000',
    paddingVertical: 10,
    borderRadius: 6,
    alignItems: 'center',
  },
  promoBuyBtnText: {
    color: '#000',
    fontSize: 12,
    fontWeight: '700',
  },
  promoCreateBtn: {
    flex: 1,
    backgroundColor: '#222',
    paddingVertical: 10,
    borderRadius: 6,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#444',
  },
  promoCreateBtnText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },

  // Featured Event
  featured: {
    height: 280,
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  featuredThumb: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  featuredOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 100%)',
  },
  featuredContent: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 20,
  },
  featuredBadges: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  featuredLiveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e11900',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    gap: 6,
  },
  featuredLiveText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '900',
  },
  featuredViewerBadge: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  featuredViewerText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
  },
  featuredStreamerBadge: {
    backgroundColor: 'rgba(252,208,0,0.9)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  featuredStreamerText: {
    color: '#000',
    fontSize: 11,
    fontWeight: '700',
  },
  featuredGame: {
    color: '#FCD000',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 4,
  },
  featuredTitle: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '900',
    marginBottom: 8,
  },
  featuredPrize: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  featuredFlood: {
    marginTop: 8,
  },
  featuredFloodText: {
    color: '#00CED1',
    fontSize: 13,
    fontWeight: '600',
  },
});